import classNames from "classnames";
import { POSITION_STATE } from "lib/constants";
import rollbar from "lib/rollbar";
import { get } from "redux/fetch";

export async function isZipNearPositions(zipCode) {
    if (!zipCode) {
        return false;
    }

    try {
        const result = await get(`/api/v1/position/nearby/${zipCode}/`);
        return result.success;
    } catch (e) {
        rollbar.error(e);
        return false;
    }
}

const POSITION_STATE_SORT = {
    [POSITION_STATE.APPROVED]: 1,
    [POSITION_STATE.ONBOARDING_COMPLETE]: 2,
    [POSITION_STATE.PENDING]: 3,
    [POSITION_STATE.INCOMPLETE]: 4,
    [POSITION_STATE.ONBOARDING_STARTED]: 5,
    [POSITION_STATE.ONBOARDING_INCOMPLETE]: 6,
    [POSITION_STATE.EXPANSION]: 7,
};

/**
 * Sort positions:
 * 1. Active positions first
 * 2. Then by position title
 * 3. Then by facility
 */
export function sortPositionsByActive(a, b) {
    if (a.active !== b.active) {
        return a.active ? -1 : 1;
    }

    const titleA = a.title.toLowerCase();
    const titleB = b.title.toLowerCase();

    if (titleA !== titleB) {
        return titleA < titleB ? -1 : 1;
    }

    return a.location_name.toLowerCase() < b.location_name.toLowerCase() ? -1 : 1;
}

/**
 * Sort positions:
 * 1. Active positions first
 * 2. Then by position state
 * 3. Then by position title
 * 4. Then by facility
 */
export function sortPositionsByActiveAndState(a, b) {
    if (a.active !== b.active) {
        return a.active ? -1 : 1;
    }

    if (a.state !== b.state) {
        return POSITION_STATE_SORT[a.state] - POSITION_STATE_SORT[b.state];
    }

    const titleA = a.title.toLowerCase();
    const titleB = b.title.toLowerCase();

    if (titleA !== titleB) {
        return titleA < titleB ? -1 : 1;
    }

    return a.location_name.toLowerCase() < b.location_name.toLowerCase() ? -1 : 1;
}

// This should match the number of colors in the @position_colors list in calendar.less
const TOTAL_POSITION_COLORS = 9;

export function getPositionColorClassNames(idx) {
    if (idx === -1) return "calendar-role-no-access calendar-role-bgcolor";
    const num = idx % TOTAL_POSITION_COLORS;
    return `calendar-role${num} calendar-role-bgcolor`;
}

export function getPositionFullTitle(position) {
    return `${position.title} - ${position.location_name}`;
}

export const getPositionStateClass = (position) =>
    classNames("portal-state", {
        "portal-state--closed": !position.active,
        "portal-state--open": position.active,
        "portal-state--pending": position.state === POSITION_STATE.PENDING,
        "portal-state--incomplete": position.state === POSITION_STATE.INCOMPLETE,
        "portal-state--obcomplete": position.state === POSITION_STATE.ONBOARDING_COMPLETE,
        "portal-state--obincomplete": position.state === POSITION_STATE.ONBOARDING_INCOMPLETE,
        "portal-state--obstarted": position.state === POSITION_STATE.ONBOARDING_STARTED,
    });

/* eslint-disable no-param-reassign */
export function mergePositionAttributes(item, position) {
    item.ws_position_id = position.id;
    item.ws_position = position.title;
    item.ws_position_location = position.location_name;
    item.ws_role_type = position.role_type_text;
    item.ws_city = position.city;
    item.ws_benchmark_category = position.benchmark_category;
    item.latitude = position.latitude;
    item.longitude = position.longitude;
    item.facility_id = position.facility_id;

    Object.entries(position.facility_attributes || {}).forEach(([k, v]) => {
        item[k] = v;
    });

    // position attributes can override facility attributes
    Object.entries(position.attributes).forEach(([k, v]) => {
        item[k] = v;
    });

    return item;
}

export function attachFacilityToPosition(position, facilities) {
    position.facility = facilities.find((f) => f.id === position.facility_id);
    if (position.facility) {
        if (position.facility.name) {
            position.location_name = position.facility.name;
        }

        if (position.facility.attributes) {
            position.facility_attributes = position.facility.attributes;
        }
    }
}
/* eslint-enable */
